<script setup lang="ts">
import type { BreadcrumbProps } from '#seo-utils/app/composables/useBreadcrumbItems'
import type { InclusionOption } from '~/types/download'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { LawCitationInfo } from '~/utils/solr/law-utils'
import { LAW_BASE_PATH } from '#shared/constants'
import { SolrParamsKey } from '#shared/solr'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import { useDownloadAccess } from '~/composables/download/useDownloadAccess'
import { useLawWithLegalCommentary } from '~/composables/laws/useLawWithLegalCommentary'
import { isHiddenLawId, pruneHiddenLawNodes } from '~/utils/law-visibility'
import { annotateLawTocNodesWithComments, mapLawTocToDownloadNodes } from '~/utils/download/mapLawTocToNodes'
import { cleanHighlights } from '~/utils/solr/highlighting-utils'
import { findTopLevelLawCommentEzId, transformShortTitle } from '~/utils/solr/law-utils'
import { buildTocChildren } from '~/utils/toc/ids'

interface Node {
  id: string
  children?: Node[] | null
}

const route = useRoute<'law-by-id'>()

const { loggedIn } = useUserSession()
const params = useLawRouteParams()
const { book, bookQuery, rootCommentary, commentaries: comments, commentariesQuery, lawQuery, legalCommentaryQuery } = useLawWithLegalCommentary()

useEditorCopySolrId()

const { canDownload, canDownloadLawText } = useDownloadAccess()
const { textLabel } = useLawTextLabels()

const searchMode = ref<boolean>(!!route.query.lcq)

const asideContext = ref<AsideContext>('commentary')

const { referenceLawId, referenceOrigin, closeReference } = useLawReferencePanel()
const { activeView, open, back, reset } = useLawView()
const { isDark: readingDarkMode, toggle: toggleReadingDarkMode } = useReadingMode()

const sidebarOpen = ref<boolean>(true)

const sidebarOpenBeforeCollapse = ref<boolean>(true)

function closeLawReference() {
  closeReference()
  back()
}

watch(activeView, (view, prev) => {
  if (!isTabletViewport()) {
    return
  }
  const isReadingLike = view === 'reading' || view === 'toc'
  if (!isReadingLike) {
    if (prev === 'reading' || prev === 'toc') {
      sidebarOpenBeforeCollapse.value = sidebarOpen.value
    }
    sidebarOpen.value = false
  }
  else {
    sidebarOpen.value = sidebarOpenBeforeCollapse.value
  }
})

watch(sidebarOpen, (isOpen) => {
  if (!isTabletViewport()) {
    return
  }
  if (isOpen && activeView.value !== 'reading') {
    if (referenceLawId.value) {
      closeReference()
    }
    reset()
  }
})

watch(referenceLawId, (id) => {
  if (id && activeView.value !== 'reference') {
    if (referenceOrigin.value === 'aside' && activeView.value === 'reading') {
      open(asideContext.value)
    }
    open('reference')
  }
})

watch(referenceLawId, (id) => {
  if (!isDesktopViewport()) {
    return
  }
  const shouldBeOpen = !id
  if (sidebarOpen.value !== shouldBeOpen) {
    sidebarOpen.value = shouldBeOpen
  }
})

watch(sidebarOpen, (isOpen) => {
  if (!isDesktopViewport()) {
    return
  }
  if (isOpen && referenceLawId.value) {
    closeReference()
  }
})

// Mobile has no collapsed sidebar, so force it open. Otherwise, it can stay stuck after collapsing on tablet
const isMobile = useMediaQuery(`(max-width: ${MOBILE_MAX_WIDTH}px)`)
watch(isMobile, (mobile) => {
  if (mobile) {
    sidebarOpen.value = true
  }
})

watch(() => params.value.lawId, () => {
  reset()
})

onScopeDispose(() => {
  reset()
})

const isInitialEntry = ref<boolean>(true)

const downloadModal = useTemplateRef('downloadModal')
const sourcesModal = useTemplateRef<{ show: () => void, hide: () => void }>('sourcesModal')

const currentNodeId = computed(() => {
  const frag = params.value.lawFragmentId
  return `${params.value.lawId}${frag ? `/${frag}` : ''}`
})

const hasLicense = useHasLicense()
const { restrictedNsLaw } = useRuntimeConfig().public.licenses
const isRestrictedNsLaw = computed(() => params.value.lawId?.toLowerCase().includes(restrictedNsLaw) ?? false)

const lawCommentaryUnlicensedDescription = computed(() => {
  if (!isRestrictedNsLaw.value) {
    return undefined
  }

  return '<p>Vårt samarbeid med <b>Standard Norge</b> gir oss endelig mulighet til å tilby deres kontrakter med Juridika sine kommentarer.</p><p>Innholdet kan kjøpes som en separat tilleggspakke, og inkluderer tilgang til kommentarutgaven til NS 8407.</p>'
})

const disableDownloadLabel = computed(() => {
  if (!loggedIn.value) {
    return 'Du må være logget inn for å laste ned dette innholdet'
  }
  if (!hasLicense.value) {
    return 'Din bruker har ikke tilgang til nedlasting'
  }
  if (!canDownload.value) {
    return 'Nedlasting ikke tilgjengelig for dette innholdet'
  }
  return undefined
})

const inclusionOptions = computed<InclusionOption[]>(() => [
  { key: 'frontPage', label: 'Forside' },
  { key: 'tableOfContents', label: 'Innholdsfortegnelse' },
  { key: 'lovtekst', label: textLabel.value },
  { key: 'curatedReferences', label: 'Forfatterens utvalgte kilder' },
  { key: 'markings', label: 'Mine markeringer og notater' },
])

watch(lawQuery.isLoading, (loading) => {
  if (!loading) {
    if (lawQuery.error.value) {
      const error = lawQuery.error.value
      if ((error as any)?.response?.status === 403) {
        if (lawCommentaryUnlicensedDescription.value) {
          showError({
            statusCode: 403,
            data: {
              customDescription: lawCommentaryUnlicensedDescription.value,
              nsRestrictedLaw: true,
            },
          })
          return
        }

        showError({ statusCode: 403, statusMessage: 'Du har ikke tilgang til dette innholdet.' })
        return
      }
      else if ((error as any)?.response?.status === 401) {
        showError({ statusCode: 401 })
        return
      }
      showError({ statusCode: 500, statusMessage: 'Internal server error' })
      return
    }
    if (lawQuery.data.value?.response?.docs?.length === 0) {
      showError({ statusCode: 404, statusMessage: 'Lov ikke funnet' })
    }

    if (isHiddenLawId(params.value.lawId)) {
      showError({ statusCode: 404, statusMessage: 'Lov ikke funnet' })
    }
  }
}, { immediate: true })

const laws = computed(() => pruneHiddenLawNodes(lawQuery.data.value?.response?.docs ?? []))

const law = computed(() => {
  const lawDoc = laws.value[0]

  if (!lawDoc?.id) {
    return null
  }

  const { title } = transformShortTitle(lawDoc.shortTitle)

  return {
    ...lawDoc,
    id: lawDoc.id,
    shortTitleWithoutAbbreviation: title,
    shortTitle: lawDoc.shortTitle || null,
  }
})

const lawCitationInfo = computed<LawCitationInfo>(() => ({
  id: law.value?.id,
  title: law.value?.title,
  shortTitle: law.value?.shortTitle,
  timestamp: law.value?.timestamp,
  dateOfPublication: law.value?.dateOfPublication,
  dateInForce: law.value?.dateInForce,
  lastChangeInForce: law.value?.lastChangeInForce,
}))

function annotateTocOrder(toc: LawAndCommentary[]): LawAndCommentary[] {
  let counter = 0

  const walk = (nodes: LawAndCommentary[]): LawAndCommentary[] => {
    return nodes.map((node) => {
      const currentIndex = counter++
      const children = node.children?.length ? walk(node.children) : []
      return {
        ...node,
        tocIndex: currentIndex,
        children,
      }
    })
  }

  return walk(toc)
}

const baseTableOfContent = computed(() => {
  return annotateTocOrder(
    annotateLawTocNodesWithComments(laws.value, comments.value, { allowParagraphFallback: false }),
  )
})

const lawTableOfContent = computed(() => {
  return filterOutNodesByTypes(baseTableOfContent.value, ['law', 'note'])
})

const isStandardText = useIsStandardText()

const { collapseAll } = useTocExpandProvider(
  lawTableOfContent,
  (node: LawAndCommentary) => buildTocChildren(node, isStandardText.value),
  (node: LawAndCommentary) => node.id,
)

// The page currently remounts when `lawId` changes, so this cannot happen. Keep this in case `definePageMeta.key` changes later.
watch(() => params.value.lawId, () => {
  collapseAll()
})

const lawTableOfContentForDownload = computed(() => {
  return filterOutNodesByTypes(
    annotateTocOrder(annotateLawTocNodesWithComments(laws.value, comments.value, { allowParagraphFallback: true })),
    ['law', 'note'],
  )
})

const lawTocForDownload = computed(() =>
  mapLawTocToDownloadNodes({
    tocNodes: lawTableOfContentForDownload.value,
    comments: comments.value as LawAndCommentary[] | null,
    lawId: law.value?.id,
    rootTitle: law.value?.title ?? 'Kommentar til loven',
    isStandardText: isStandardText.value,
  }),
)

const lawCommentId = computed(() => findTopLevelLawCommentEzId(comments.value, law.value?.id))
const commentNodeIds = computed(() => new Set(comments.value.map(it =>
  `${params.value.lawId}${it.lawFragmentId ? `/${it.lawFragmentId}` : ''}`.toLocaleLowerCase(),
)))

const commentsWithCuratedReferences = computed(() =>
  new Set(
    comments.value
      .filter(comment => comment.curatedReferences?.length)
      .map(comment =>
        `${params.value.lawId}${comment.lawFragmentId ? `/${comment.lawFragmentId}` : ''}`.toLocaleLowerCase(),
      ),
  ),
)

const commentsWithRelatedLiterature = computed(() =>
  new Set(
    comments.value
      .filter(comment => comment.editionId)
      .map(comment =>
        `${params.value.lawId}${comment.lawFragmentId ? `/${comment.lawFragmentId}` : ''}`.toLocaleLowerCase(),
      ),
  ),
)

const selectedComment = computed(() => legalCommentaryQuery.data.value?.response?.docs[0])
const literatureListParams = computed(() => (loggedIn.value && hasLicense.value)
  ? { [SolrParamsKey.LiteratureListByLawId]: params.value.lawId }
  : {})
const literatureListQuery = useLawCommentaryQuery('law-commentary', literatureListParams)
const literatureList = computed(() => literatureListQuery.data.value?.response?.docs[0])
const hasLiteratureList = computed(() => Boolean(literatureList.value))
const asideComment = selectedComment

const selectedSearchResultLaw = ref<{ id: string, highlights: string[] } | null>(null)
const selectedSearchResultComment = ref<{ id: string, highlights: string[], ezMainNodeId?: number | null } | null>(null)
const allLawSearchResults = ref<Array<{ id: string, highlights: string[] }>>([])
const allCommentarySearchResults = ref<Array<{ id: string, highlights: string[], ezMainNodeId: number | null }>>([])

function onSearchResultsLoaded(results: Array<{ id: string, highlights: string[], isComment: boolean, ezMainNodeId?: number | null }>) {
  allLawSearchResults.value = results
    .filter(r => !r.isComment)
    .map(r => ({ id: r.id, highlights: cleanHighlights(r.highlights) }))
  allCommentarySearchResults.value = results
    .filter(r => r.isComment)
    .map(r => ({ id: r.id, highlights: cleanHighlights(r.highlights), ezMainNodeId: r.ezMainNodeId ?? null }))
}

function openDownloadModal() {
  downloadModal.value?.show?.({
    id: currentNodeId.value,
    ezMainNodeId: selectedComment.value?.ezMainNodeId ?? undefined,
  })
}

function openSourcesModal() {
  sourcesModal.value?.show?.()
}

function showSearchResult(id: string, highlights: string[], isComment: boolean, ezMainNodeId?: number | null) {
  const cleanedHighlights = cleanHighlights(highlights)

  if (!isComment) {
    selectedSearchResultLaw.value = { id, highlights: cleanedHighlights }
    open('reading')
  }
  else {
    selectedSearchResultComment.value = { id, highlights: cleanedHighlights, ezMainNodeId }
    open('commentary')
  }
}

const edition = computed(() => {
  const lawIdSegments = route.params.lovId || []
  const lastSegment = lawIdSegments[lawIdSegments.length - 1] // e.g., '5.utgave'
  return lastSegment?.replace(/\s*(utgave)/g, ' $1')
})

const isAboutBookPath = useIsAboutBookPath()

const title = computed(() => law.value?.shortTitleWithoutAbbreviation ?? 'Lov')

// Compute number of segments in the path
const pathSegments = computed(() => {
  const normalizedPath = route.path.endsWith('/') ? route.path.slice(0, -1) : route.path
  return normalizedPath.split('/').filter(Boolean)
})

// Check if route matches the base law route
const isBaseLawRoute = computed(() => {
  const baseLawRoute = `/${LAW_BASE_PATH}`
  return pathSegments.value.length === 2 && route.path.startsWith(baseLawRoute)
})

function generateFalseArray(length: number) {
  return Array.from({ length }).fill(false) as false[]
}

const breadcrumbOverrides = computed((): BreadcrumbProps['overrides'] =>
  isAboutBookPath.value
    ? [
        undefined,
        false,
        {
          label: title.value,
          to: `/${LAW_BASE_PATH}/${params.value.lawId}`,
        },
        {
          label: edition.value ?? '',
          to: route.path,
        },
        // Skip all additional segments just in case
        ...generateFalseArray(Math.max(0, pathSegments.value.length - 4)),
      ]
    : [
        undefined,
        {
          label: 'Lover og lovkommentarer',
          to: `/${LAW_BASE_PATH}`,
        },
        {
          label: title.value,
          to: `/${LAW_BASE_PATH}/${params.value.lawId}`,
        },
        // Skip all additional segments
        ...generateFalseArray(pathSegments.value.length - 2),
      ],
)

watch(() => route.path, () => {
  if (isInitialEntry.value && isBaseLawRoute.value) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    isInitialEntry.value = false
  }
})

// Sync search mode with lcq parameter, but only trigger search mode if lcq actually changes to avoid triggering search mode on back/forward navigation
watch([() => route.query.lcq, () => route.path], ([lcq], [prevLcq]) => {
  if (lcq && lcq !== prevLcq) {
    searchMode.value = true
  }
  else if (!lcq) {
    searchMode.value = false
  }
})

// Clear search results when search mode is exited (route change or toggle button)
watch(searchMode, (active) => {
  if (!active) {
    selectedSearchResultComment.value = null
    selectedSearchResultLaw.value = null
  }
})

const contentDiv = useTemplateRef<ComponentPublicInstance | null>('contentDiv')
const { contentTop, contentBottom, headerHeight, updateMeasurements, bannerHeight } = useContentMeasurements(contentDiv)

const lawAndDescendantIds = computed(() => {
  const ids = new Set<string>()
  if (!law.value) {
    return ids
  }

  const nodesToVisit: Node[] = [{ id: law.value.id, children: law.value.children }]

  while (nodesToVisit.length) {
    const currentNode = nodesToVisit.pop()
    if (!currentNode) {
      continue
    }

    ids.add(String(currentNode.id.toLowerCase()))

    const children = currentNode.children ?? []
    // Try to keep the same order as children originally have
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i]
      if (child) {
        nodesToVisit.push(child)
      }
    }
  }

  return ids
})

const selectedNodeId = computed(() => {
  const urlNodeId = params.value.nodeId?.toLocaleLowerCase()
  const paragraph = params.value.paragraph

  if (urlNodeId) {
    const exact = lawAndDescendantIds.value.has(urlNodeId)

    if (exact) {
      return urlNodeId.toLocaleLowerCase()
    }
  }

  if (paragraph) {
    const suffix = String(paragraph).toLowerCase()

    // We assume we want the first id that ends with our current paragraph param, and not paragraphs/articles in
    // for instance attachments to the law (that can match the same paragraph as in the law text)
    for (const id of lawAndDescendantIds.value) {
      if (id.endsWith(suffix)) {
        return id
      }
    }
  }

  return null
})

const selectedPartTitle = computed(() => {
  const selectedId = selectedNodeId.value

  if (!selectedId) {
    return params.value.lawFragmentId
  }

  const findNodePath = (nodes: LawAndCommentary[]): LawAndCommentary[] | null => {
    for (const node of nodes) {
      if (node.id.toLocaleLowerCase() === selectedId) {
        return [node]
      }

      const childPath = findNodePath(node.children ?? [])
      if (childPath) {
        return [node, ...childPath]
      }
    }

    return null
  }

  const nodePath = findNodePath(laws.value)
  return nodePath?.reverse().find(node => node.title?.trim())?.title ?? params.value.lawFragmentId
})

type EmptyCommentaryState = 'no-selection' | 'law-without-comments' | 'part-without-comment'

const emptyCommentaryState = computed<EmptyCommentaryState | null>(() => {
  if (
    lawQuery.isLoading.value
    || commentariesQuery.isLoading.value
    || legalCommentaryQuery.isLoading.value
    || selectedComment.value
  ) {
    return null
  }

  if (!comments.value.length) {
    return 'law-without-comments'
  }

  return params.value.lawFragmentId ? 'part-without-comment' : 'no-selection'
})

onMounted(() => {
  isInitialEntry.value = true
  searchMode.value = !!route.query.lcq
  if (isMobileViewport() && route.query.lcq) {
    open('toc')
  }
  setTimeout(() => updateMeasurements(), 100)
})

useTrackLastActivity(title)

usePendoContentTracking({
  type: 'law',
  title: computed(() => law.value?.shortTitleWithoutAbbreviation ?? ''),
  id: computed(() => params.value.lawId),
  legalAreas: computed(() => {
    if (!lawQuery.isFetched.value || !commentariesQuery.isFetched.value) {
      return null
    }

    return comments.value.flatMap(comment => comment.legal_areas ?? [])
  }),
})

definePageMeta({
  name: 'law-by-id',
  key: route => `law-${(route.params as { lovId?: string[] }).lovId?.[0] ?? ''}`,
  layout: 'default-with-mobile-notice',
  theme: 'carbon',
  mobileReady: true,
})

useHead({
  title,
  bodyAttrs: {
    class: computed(() => isAboutBookPath.value ? 'bg-white' : 'bg-surface-primary reading-dark-scope'),
  },
})
</script>

<template>
  <div>
    <h1 class="sr-only">
      {{ title }}
    </h1>
    <ContentDiv ref="contentDiv" class="flex items-center">
      <div class="md:hidden flex items-center gap-2xs">
        <ControlButton
          size="small"
          :icon="faArrowLeft"
          icon-size="md"
          aria-label="Tilbake"
          class="!size-11 bg-transparent hover:bg-surface-secondary dark:hover:bg-surface-secondary border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blurple-300"
          @click="navigateTo(`/${LAW_BASE_PATH}`)"
        />
      </div>
      <Breadcrumbs :overrides="breadcrumbOverrides" hide-mobile-back />
      <ReadingActions
        v-if="!isAboutBookPath"
        :content="law"
        :loading="lawQuery.isLoading.value"
        :hide-download-button="!loggedIn"
        :hide-about-book-button="!book"
        :show-sources-button="loggedIn && hasLicense && hasLiteratureList"
        :disable-download-label="disableDownloadLabel"
        enable-dark-mode
        :dark-mode="readingDarkMode"
        @open-download="openDownloadModal"
        @open-sources="openSourcesModal"
        @update:dark-mode="toggleReadingDarkMode"
      />
    </ContentDiv>

    <template v-if="isAboutBookPath">
      <LiteratureDescriptionSkeleton v-if="bookQuery.isLoading.value" />
      <LiteratureDescription v-else-if="book" :book="book" type="law-commentary" />
    </template>

    <ReadingLayout
      v-if="!isAboutBookPath"
      v-model:sidebar-expanded="sidebarOpen"
      fixed-content
      :collapsible-aside="emptyCommentaryState !== null"
      :is-loading="lawQuery.isLoading.value"
      :content-top="contentTop"
      :content-bottom="contentBottom"
      :header-height="headerHeight"
      :banner-height="bannerHeight"
      :licensed-aside="hasLicense"
      context="law"
      :active-view="activeView"
      :reading-text-label="textLabel"
    >
      <template #sidebar>
        <LawMobileTableOfContentBar v-model:search-mode="searchMode" mode="close" />

        <TableOfContentToggle
          v-model:search-mode="searchMode"
          show-search-option
          show-content-option
          show-expand-controls
          class="hidden md:flex sticky top-0 z-[9] bg-surface-elevated rounded-md pb-xs"
        />
        <div class="flex flex-col animate-fade">
          <LawSearch
            v-if="searchMode"
            :search-mode="searchMode"
            :law-comment-id="lawCommentId"
            @show-search-result="showSearchResult"
            @search-results-loaded="onSearchResultsLoaded"
          />
          <template v-else-if="!lawQuery.isLoading.value">
            <LawRepealed v-if="law?.isRepealed" :law="law" />
            <LawInfoNotice v-if="rootCommentary?.infoNoticeObjectId" :object-id="rootCommentary.infoNoticeObjectId"/>
            <ul class="mb-m">
              <LawTableOfContent
                v-for="node in lawTableOfContent"
                :key="node.id"
                :node="node"
                :part-number="0"
              />
            </ul>
          </template>
          <ul v-else class="flex flex-col gap-m">
            <li v-for="i in 20" :key="i">
              <div class="flex items-center gap-3xs text-center">
                <Skeleton class="w-4 h-4 px-2xs rounded" />
                <Skeleton class="text-small rounded-lg flex w-full items-center text-left p-2xs h-7" />
              </div>
            </li>
          </ul>
        </div>
      </template>

      <template #sidebar-collapsed>
        <span class="text-small text-carbon-500 dark:text-carbon-50 select-none font-bold">Innholdsfortegnelse og søk</span>
      </template>

      <template #content="{ isScrollTop }">
        <LawMobileTableOfContentBar v-model:search-mode="searchMode" mode="open" />

        <div v-if="lawQuery.isLoading.value" class="p-l space-y-8">
          <div v-for="i in 5" :key="i">
            <Skeleton class="h-7 w-1/3 mb-s" />
            <Skeleton v-for="j in 3" :key="j" class="h-4 w-full mb-2xs" />
            <Skeleton class="h-4 w-2/3" />
          </div>
        </div>
        <LawText
          v-for="node in laws"
          v-else
          :key="node.id"
          v-model:aside-context="asideContext"
          :node="node"
          :is-scroll-top="isScrollTop"
          :search-result="selectedSearchResultLaw"
          :search-results="allLawSearchResults"
          :selected-node-id="selectedNodeId"
          :comments="commentNodeIds"
          :law="lawCitationInfo"
          :comments-with-curated-references="commentsWithCuratedReferences"
          :comments-with-related-literature="commentsWithRelatedLiterature"
          :search-mode="searchMode"
        />
      </template>

      <template #content-collapsed>
        <span class="text-small text-carbon-500 dark:text-carbon-50 select-none font-bold">{{ textLabel }}</span>
      </template>

      <template #aside>
        <div v-if="lawQuery.isLoading.value || commentariesQuery.isLoading.value || legalCommentaryQuery.isLoading.value" class="flex flex-col gap-m">
          <div class="sticky top-0 z-[9] py-m flex flex-col xl:flex-row justify-between xl:items-center gap-m">
            <Skeleton class="inline h-4 w-32" />
            <Skeleton class="h-6 w-32 rounded" />
          </div>
          <div class="flex flex-col gap-l">
            <Skeleton class="h-4 w-48" />
            <Skeleton class="h-5 w-1/4 mb-s" />
            <Skeleton v-for="i in 6" :key="i" class="h-4 w-full mb-2xs" />
            <Skeleton class="h-4 w-3/4 mb-s" />
            <Skeleton class="h-5 w-1/3 mb-s" />
            <Skeleton v-for="i in 5" :key="i" class="h-4 w-full mb-2xs" />
            <Skeleton class="h-4 w-2/3" />
            <Skeleton v-for="i in 4" :key="i" class="h-4 w-full mb-2xs" />
          </div>
        </div>
  <ReadingNotAuthorized v-else-if="!loggedIn || !hasLicense" title="Lovkommentar" scope="any" />
  <div v-else-if="emptyCommentaryState" class="text-caption text-carbon-500">
          <EmptyCommentaryState v-if="emptyCommentaryState === 'no-selection'" illustration="man-pointing">
            <p class="text-lg font-semibold">Ingen kommentar er valgt</p>
            <p class="text-base">Velg en del merket med <span class="font-semibold">Kommentar →</span> for å lese</p>
          </EmptyCommentaryState>

          <EmptyCommentaryState v-else-if="emptyCommentaryState === 'law-without-comments'" illustration="feather-pen" gap="xl">
            <div class="flex flex-col gap-s">
              <p class="text-lg font-semibold max-w-md text-center">{{ title }} har ingen lovkommentarer</p>
              <p class="text-lg max-w-md text-center">Det finnes ingen lovkommentarer til denne loven på Juridika enda.</p>
            </div>
            <NuxtLink :to="`/${LAW_BASE_PATH}`" class="text-base underline font-medium">
              Se alle lover med lovkommentarer
            </NuxtLink>
          </EmptyCommentaryState>

          <EmptyCommentaryState v-else-if="emptyCommentaryState === 'part-without-comment'" illustration="man-pointing">
            <p class="text-lg font-semibold max-w-md">{{ selectedPartTitle }} har ingen kommentar</p>
            <p class="text-base max-w-md text-center">Denne delen har ikke kommentar enda. Delene som har kommentar er merket med <span class="font-semibold">Kommentar →</span></p>
          </EmptyCommentaryState>
        </div>

        <template v-else-if="asideComment">
          <LawAsideHeader
            :law="law"
            :context="asideContext"
            :comment="asideComment"
            @close="asideContext = 'commentary'"
          />
          <LawCuratedReferenceList
            v-if="asideContext === 'curated' && asideComment.curatedReferences?.length"
            v-model:aside-context="asideContext"
            :curated-references="asideComment.curatedReferences"
            :title="title"
          />
          <LawRelatedLiterature
            v-else-if="asideContext === 'literature' && book"
            v-model:aside-context="asideContext"
            :book="book"
          />
          <LawCommentary
            v-else
            :comment="asideComment"
            :root-commentary="rootCommentary"
            :search-result="selectedSearchResultComment"
            :search-results="allCommentarySearchResults"
            :law="lawCitationInfo"
          />
        </template>
      </template>

      <template #aside-collapsed>
        <span class="text-small text-carbon-500 dark:text-white select-none font-bold">Lovkommentar</span>
      </template>

      <template v-if="referenceLawId" #reference>
        <LawReferencePanel :law-id="referenceLawId" @close="closeLawReference" />
      </template>
    </ReadingLayout>
    <DownloadModal
      v-if="hasLicense"
      ref="downloadModal"
      :toc="lawTocForDownload"
      :inclusion-options="inclusionOptions"
      :selected-id="currentNodeId"
      :disable-lovtekst="!canDownloadLawText(params.lawId)"
      render-leaf-nodes
    />
    <LawSourcesModal
      v-if="hasLicense"
      ref="sourcesModal"
      :literature-list="literatureList"
      :is-loading="literatureListQuery.isLoading.value"
    />
  </div>
</template>
